import { HttpHeaders } from "@angular/common/http";
import { inject, Service } from "@angular/core";
import { filter } from "rxjs";

import { AdaaHelper } from "../../core/utils";
import {
  AggregationPerfModelType,
  AiPerformanceModel,
  GetCalculationValuesArgs,
  GovEnablerDirectionsModelType,
  GovEnablerModelType,
  GovKpiVisionModelType,
  GraphByAggregationTypeModelType,
  MainResponseModel,
  MasterEkpiActuals,
  ObjectTypeAnnualPerformanceType,
  PerfColorCodeModelType,
  PerformanceConfigType,
  PerformanceOverYearModelType,
  PillarPerformanceModelType,
  SDGModelType,
  VisionDirectionModelType,
} from "../models";
import { MainService } from "./main-service";

@Service()
export class CalculationEngineApiService {
  private _mainService = inject(MainService);

  public getGovDirections({ period, isAnnual = false }: { period: number; isAnnual?: boolean }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/directions/${period}`;

    return this._mainService.sendRequest<MainResponseModel<GovEnablerDirectionsModelType[]>>({
      method: "GET",
      url,
    });
  }

  public getGovEnablerPillars({ period, isAnnual = false }: { period: number; isAnnual?: boolean }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/pillars/${period}`;

    return this._mainService.sendRequest<MainResponseModel<GovEnablerModelType>>({
      method: "GET",
      url,
    });
  }

  public getVisionDirections({ period, isAnnual = false }: { period: number; isAnnual?: boolean }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/visions/${period}`;

    return this._mainService.sendRequest<MainResponseModel<VisionDirectionModelType[]>>({
      method: "GET",
      url,
    });
  }

  public getVisionDirectionsV2({ period, isAnnual = false }: { period: number; isAnnual?: boolean }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/visionsV2/${period}`;

    return this._mainService.sendRequest<MainResponseModel<VisionDirectionModelType[]>>({
      method: "GET",
      url,
    });
  }

  public getPMOPerformance({
    period,
    isAnnual = false,
    overallPerformanceContributing = false,
    entitySize = undefined,
    entityType = undefined,
  }: {
    period: number;
    isAnnual?: boolean;
    overallPerformanceContributing?: boolean;
    entitySize?: number;
    entityType?: number;
  }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/${entitySize || entityType ? "calculateGovPerformances" : "getGovernmentPerformance"}/${period}`;

    const qs = new URLSearchParams();
    if (entityType) qs.append("entityType", entityType.toString());
    if (entitySize) qs.append("entitySize", entitySize.toString());
    if (overallPerformanceContributing) qs.append("overallPerformanceContributing", "true");

    return this._mainService.sendRequest<MainResponseModel<AggregationPerfModelType[]>>({
      method: "GET",
      url: `${url}?${qs.toString()}`,
    });
  }

  public getEntityPerformance({
    entityId,
    period,
    isAnnual = false,
    overallPerformanceContributing = false,
  }: {
    period: number;
    entityId: number;
    isAnnual?: boolean;
    overallPerformanceContributing?: boolean;
  }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/getEntityPerformance`;

    const qs = new URLSearchParams();
    if (overallPerformanceContributing) qs.append("overallPerformanceContributing", "true");

    return this._mainService.sendRequest<MainResponseModel<AggregationPerfModelType[]>>({
      url: `${url}/${entityId}/${period}?${qs}`,
      method: "GET",
    });
  }

  public getPreformanceOverYears({
    aggregationType = undefined,
    entityId = undefined,
    entitySize = undefined,
    entityType = undefined,
  }: {
    aggregationType?: number;
    entityId?: number;
    entitySize?: number;
    entityType?: number;
  }) {
    let url = `calculationreadingannual${AdaaHelper.isPMOEntity() ? "/governments/yearly" : "/entities/rank"}`;

    if (AdaaHelper.isPMOEntity() && (entitySize || entityType))
      url = `calculationreadingannual/calculateGovernments/yearly`;

    const qs = new URLSearchParams();

    if (aggregationType) qs.append("aggregationType", aggregationType.toString());
    if (entityId) qs.append("entityId", entityId.toString());
    if (entityType) qs.append("entityType", entityType.toString());
    if (entitySize) qs.append("entitySize", entitySize.toString());

    return this._mainService.sendRequest<MainResponseModel<PerformanceOverYearModelType>>({
      url: `${url}?${qs}`,
      method: "GET",
    });
  }

  public getSDG({ period, isAnnual = false }: { period: number; isAnnual?: boolean }) {
    const url = `${isAnnual ? "calculationreadingannual" : "calculationreading"}/sdg/${period}`;

    return this._mainService.sendRequest<MainResponseModel<SDGModelType>>({
      method: "GET",
      url,
    });
  }

  public getCalculationReading({
    itemId,
    objectType,
    periodId,
    entityId,
  }: {
    itemId: number;
    objectType: number;
    periodId: number;
    entityId?: number;
  }) {
    const qs = new URLSearchParams();
    if (entityId) qs.append("entityId", entityId.toString());

    const url = `calculationreading/get/${itemId}/${objectType}/${periodId}`;

    return this._mainService
      .sendRequest<MainResponseModel<ObjectTypeAnnualPerformanceType>>({
        method: "GET",
        url: `${url}?${qs.toString()}`,
        ignoreNotifications: true,
      })
      .pipe(filter((res) => !res.inError));
  }

  public getCalculationReadingAnnual({
    itemId,
    objectType,
    year,
  }: {
    itemId: number;
    objectType: number;
    year: number;
  }) {
    const url = `calculationreadingannual/get/${itemId}/${objectType}/${year}`;

    return this._mainService.sendRequest<MainResponseModel<ObjectTypeAnnualPerformanceType>>({
      method: "GET",
      url,
      ignoreNotifications: true,
    });
  }

  public getGovPerformanceConfig() {
    const url = `performanceconfigdetail/government`;

    return this._mainService.sendRequest<MainResponseModel<PerformanceConfigType[]>>({
      method: "GET",
      url,
    });
  }

  public getEntityPerformanceConfig(entityId: number) {
    const url = `performanceconfigdetail/entity/${entityId}`;

    return this._mainService.sendRequest<MainResponseModel<PerformanceConfigType[]>>({
      method: "GET",
      url,
    });
  }

  public performanceColors() {
    const url = `calculationengine/performancecolors`;

    return this._mainService
      .sendRequest<MainResponseModel<PerfColorCodeModelType[]>>({
        method: "GET",
        url,
      })
      .pipe(filter((res) => !res.inError));
  }

  public getKpiVisionData({
    kpiType,
    govDirId,
    periodId,
    subGovDirId,
  }: {
    kpiType: number;
    govDirId: number;
    periodId: number;
    subGovDirId?: number;
  }) {
    const url = `calculationreading/kpiVisions`;
    const qs = new URLSearchParams({
      kpiType: `${kpiType}`,
      govDirId: `${govDirId}`,
      periodId: `${periodId}`,
    });

    if (subGovDirId) qs.set("subGovDirId", `${subGovDirId}`);

    return this._mainService
      .sendRequest<MainResponseModel<GovKpiVisionModelType[]>>({
        method: "GET",
        url: `${url}?${qs.toString()}`,
      })
      .pipe(filter((res) => !res.inError));
  }

  public getCalculationData(params: GetCalculationValuesArgs) {
    const qs = new URLSearchParams();
    const headers = new HttpHeaders();

    qs.append("type", params.type.toString());

    if (params.periodId) qs.append("periodId", params.periodId.toString());
    if (params.year) qs.append("year", params.year.toString());

    if (params.governmentAggregationType)
      qs.append("governmentAggregationType", params.governmentAggregationType.toString());
    if (params.entityAggregationType) qs.append("entityAggregationType", params.entityAggregationType.toString());
    if (params.pillarId) qs.append("pillarId", params.pillarId.toString());

    if (params.entitySize) qs.append("entitySize", params.entitySize.toString());
    if (params.entityType) qs.append("entityType", params.entityType.toString());

    if (params.entityId) headers.append("entityId", params.entityId.toString());

    return this._mainService.sendRequest<MainResponseModel<unknown>>({
      method: "GET",
      headers: headers,
      url: `calculations/values?${qs.toString()}`,
    });
  }

  public getGraphByByAggregationType({
    itemTypeId,
    graph,
    frequency,
    entityId,
    periodId,
  }: {
    itemTypeId: number;
    graph: string;
    frequency: string;
    entityId: number;
    periodId: number;
  }) {
    const url = `calculationreading/getCalculationReadingsGraphCardByByAggregationType`;
    const qs = new URLSearchParams();

    qs.append("entityId", `${entityId}`);

    if (periodId) {
      qs.append("periodId", `${periodId}`);
    }

    return this._mainService.sendRequest<MainResponseModel<GraphByAggregationTypeModelType[]>>({
      url: `${url}/${itemTypeId}/${graph}/${frequency}?${qs.toString()}`,
      method: "POST",
      data: {},
    });
  }

  public getPillarPerformance(periodId: number, entityId: number, isQuarter: boolean) {
    const url = `pillar/getPillarsPerformanceByEntity`;
    const qs = new URLSearchParams();
    qs.append("entityId", `${entityId}`);
    qs.append(isQuarter ? "periodId" : "year", `${periodId}`);

    return this._mainService.sendRequest<MainResponseModel<PillarPerformanceModelType[]>>({
      method: "GET",
      url: `${url}?${qs.toString()}`,
    });
  }

  public getEkpiActuals(ekpiId: number, year: number) {
    const url = `calculationreadingannual/ekpi-actuals`;
    const qs = new URLSearchParams();

    AdaaHelper.isPMOEntity() ? qs.append("masterEkpiId", `${ekpiId}`) : qs.append("ekpiId", `${ekpiId}`);
    qs.append("year", `${year}`);

    return this._mainService.sendRequest<MainResponseModel<MasterEkpiActuals>>({
      method: "GET",
      url: `${url}?${qs.toString()}`,
    });
  }

  public getAIPerformance(periodId: number, isQuarter: boolean) {
    return this._mainService.sendRequest<MainResponseModel<AiPerformanceModel>>({
      method: "GET",
      url: isQuarter
        ? `/calculationreading/getAIPerformances?periodId=${periodId}`
        : `/calculationreadingannual/getAIPerformances?year=${periodId}`,
    });
  }
}
